import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';

import { useAuth } from '../api/AuthContext.js';
import { createParticipant } from '../api/createParticipant.js';
import { fetchAllParticipantsByOwner } from '../api/fetchParticipants.js';
import ParticipantOverview from '../components/ParticipantOverview.js';
import { showToast, updateToast } from '../utils/toastUtils.js';
import * as utils from '../utils/utils.js';
import { exportParticipantsToExcel } from '../utils/export-to-excel.js';

function Dashboard() {
    const auth = useAuth();
    const [participants, setParticipants] = useState(null);
    const [showParticipantNewForm, setShowParticipantNewForm] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [errors, setErrors] = useState({ name: '', email: '' });
    const [searchTerm, setSearchTerm] = useState('');

    const getParticipants = async () => {
        const participantsData = await fetchAllParticipantsByOwner(
            auth.currentUser.user_id
        );
        setParticipants(participantsData);
    };

    const handleAddParticipant = async (e) => {
        e.preventDefault();

        // Reset errors
        setErrors({ name: '', email: '' });

        // Validate fields
        let formIsValid = true;
        if (!name) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                name: 'Name is required',
            }));
            formIsValid = false;
        }
        if (!email) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                email: 'Email is required',
            }));
            formIsValid = false;
        }

        if (formIsValid) {
            const toastId = showToast('One moment...', auth.currentUser.email, {
                toastId: 'toastIdCreateParticipant',
            });
            // This needs to be more robust to be able to calculate birthdate safely.
            const ssn = document
                .getElementById('ssn')
                .value.replace('-', '')
                .trim();

            const participantData = {
                name: name,
                nickname: document.getElementById('nickname').value,
                email: email,
                mobile_number: document.getElementById('phone').value,
                ssn: ssn,
                birthdate: ssn ? utils.obtainBirthdateFromSsn(ssn) : null,
                owner_id: auth.currentUser.user_id,
            };

            const res = await createParticipant(participantData);
            if (res && res.status === 201) {
                updateToast(toastId, auth.currentUser.email, {
                    render: 'Participant created',
                    type: 'success',
                    isLoading: false,
                    autoClose: 1500,
                });
                setName('');
                setEmail('');
                document.getElementById('name').value = '';
                document.getElementById('email').value = '';
                document.getElementById('nickname').value = '';
                document.getElementById('phone').value = '';
                document.getElementById('ssn').value = '';

                getParticipants();
            } else {
                updateToast(toastId, auth.currentUser.email, {
                    render: res ? res.data.message : 'No reply from server',
                    type: 'error',
                    autoClose: 2000,
                    isLoading: false,
                });
            }
        }
    };

    useEffect(() => {
        getParticipants();
    }, []);

    const handleAdd = () => {
        setShowParticipantNewForm(!showParticipantNewForm);
    };

    const tooltipOverview =
        'Participants are the people you register to attend your events. ' +
        '"Without participants there, no events, can be." - Yoda.';

    // Filter participants based on search term
    const filteredParticipants = participants?.filter((participant) => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            participant.name?.toLowerCase().includes(searchLower) ||
            participant.nickname?.toLowerCase().includes(searchLower) ||
            participant.email?.toLowerCase().includes(searchLower)
        );
    });

    return (
        <>
            <h1 id="participantsPage">
                Participants{' '}
                <Tooltip title={tooltipOverview}>
                    <InfoCircleOutlined />
                </Tooltip>
            </h1>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    gap: '12px',
                }}
            >
                <div style={{ flex: 1, maxWidth: '400px', marginTop: '10px', position: 'relative' }}>
                    <input
                        className="input"
                        type="text"
                        placeholder="Search participants by name, nickname, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            fontSize: '14px',
                            paddingRight: searchTerm ? '35px' : '10px',
                        }}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            style={{
                                position: 'absolute',
                                right: '8px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#999',
                                fontSize: '18px',
                                lineHeight: '1',
                                transition: 'color 0.2s ease',
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#333'}
                            onMouseLeave={(e) => e.target.style.color = '#999'}
                            aria-label="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>
                <button
                    onClick={() => exportParticipantsToExcel(participants)}
                    className="button is-light"
                    size="small"
                    style={{
                        fontSize: '14px',
                    }}
                >
                    <span className="icon">
                        <img
                            src="/img/icon-download.svg"
                            alt="Download"
                            width="16"
                            height="16"
                        />
                    </span>
                    <span>Export (.xlsx)</span>
                </button>
            </div>
            {window.location.search === '?welcome' && (
                <>
                    <span id="newUserIntro">
                        Before creating your first event you need to create a
                        few participants. <br />
                        <br />
                        Press the 'Add participant' button to get started...
                    </span>
                    <br />
                    <br />
                </>
            )}
            <div className="block list">
                {participants && (
                    <>
                        {searchTerm && (
                            <div style={{
                                fontSize: '13px',
                                color: '#666',
                                margin: '4px 4px 12px 4px',
                                padding: '4px',
                            }}>
                                Found {filteredParticipants.length} participant{filteredParticipants.length !== 1 ? 's' : ''}
                            </div>
                        )}
                        {filteredParticipants.length > 0 ? (
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: '#f9fafb',
                                        borderBottom: '2px solid #e5e7eb'
                                    }}>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'left',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}></th>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'left',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>Name</th>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'center',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>Status</th>
                                        <th style={{
                                            padding: '3px 8px',
                                            textAlign: 'right',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            color: '#6b7280',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em'
                                        }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredParticipants.map((participant) => (
                                        <ParticipantOverview
                                            key={participant.participant_id}
                                            participant={participant}
                                            refreshParticipants={getParticipants}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{
                                color: '#999',
                                fontStyle: 'italic',
                                padding: '20px',
                                textAlign: 'center'
                            }}>
                                No participants found matching "{searchTerm}"
                            </p>
                        )}
                    </>
                )}
                {showParticipantNewForm && (
                    <div style={{
                        backgroundColor: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '20px',
                        marginBottom: '16px'
                    }}>
                        <h3 style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1f2937',
                            marginBottom: '16px'
                        }}>Add New Participant</h3>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '16px',
                            marginBottom: '16px'
                        }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '4px'
                                }}>Name *</label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    placeholder="Enter name"
                                    onChange={(e) => setName(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontSize: '13px',
                                        border: errors.name ? '1px solid #ef4444' : '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = errors.name ? '#ef4444' : '#d1d5db'}
                                />
                                {errors.name && (
                                    <p style={{
                                        fontSize: '11px',
                                        color: '#ef4444',
                                        marginTop: '4px'
                                    }}>{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '4px'
                                }}>Nickname</label>
                                <input
                                    id="nickname"
                                    name="nickname"
                                    type="text"
                                    placeholder="Enter nickname"
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontSize: '13px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '4px'
                                }}>Email *</label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="Enter email"
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontSize: '13px',
                                        border: errors.email ? '1px solid #ef4444' : '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = errors.email ? '#ef4444' : '#d1d5db'}
                                />
                                {errors.email && (
                                    <p style={{
                                        fontSize: '11px',
                                        color: '#ef4444',
                                        marginTop: '4px'
                                    }}>{errors.email}</p>
                                )}
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '4px'
                                }}>Phone</label>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    placeholder="Enter phone"
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontSize: '13px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    marginBottom: '4px'
                                }}>SSN / Kennitala</label>
                                <input
                                    id="ssn"
                                    name="ssn"
                                    type="text"
                                    placeholder="Enter SSN"
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        fontSize: '13px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                id="saveParticipant"
                                onClick={handleAddParticipant}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                            >
                                Save Participant
                            </button>
                            <button
                                id="cancelEditParticipant"
                                onClick={() => setShowParticipantNewForm(false)}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    backgroundColor: 'white',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f3f4f6';
                                    e.target.style.borderColor = '#9ca3af';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'white';
                                    e.target.style.borderColor = '#d1d5db';
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
                </div>
            <button
                id="addParticipant"
                className="button is-primary is-small"
                onClick={handleAdd}
            >
                + Add participant
            </button>
            {window.location.search === '?welcome' && (
                <>
                    <span>
                        Once you have finished adding Participants, click the
                        'Events' link (in the column on the left) to add some
                        events they can attend. <br />
                        <br />
                    </span>
                    <br />
                    <br />
                </>
            )}
        </>
    );
}

export default Dashboard;
